import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import { ChartScale, ChartType, GaugeData, Language, Scale } from "..";
import { useEffect, useRef, useState } from "react";
import { useAppSelector } from "../../../../app/hooks";
import { DataType, selectData } from "../../../../features/data/dataSlice";
import { v4 as uuidv4 } from "uuid";
import _ from "lodash";
import {
  English,
  French,
  German,
  Dutch,
  Spanish,
  Italian,
  Romanian,
  Portuguese,
} from "../../../../dictionary/ChartsText";
import { LanguageCheck } from "../../../../utils/LanguageCheck";
import { selectLanguage } from "../../../../features/user/userSlice";
import moment from "moment";
import Skeleton from "react-loading-skeleton";
import Spinner from "../../../Spinner";

type Props = {
  ChartData: ChartType;
  FetchSuccess: boolean;
  FetchError: boolean;
};

function Value({ ChartData, FetchSuccess, FetchError }: Props) {

  const stateLang = useAppSelector(selectLanguage);
  let [language, setLanguage] = useState(LanguageCheck(English, French, German, Dutch, Spanish, Italian, Romanian, Portuguese, stateLang));
  useEffect(() => {
    setLanguage(LanguageCheck(English, French, German, Dutch, Spanish, Italian, Romanian, Portuguese, stateLang));
  }, [stateLang]);

  const defaultYAxis: Scale = { min_auto: true, max_auto: true, max: 0, min: 0 };

  const [dataValue, setDataValue] = useState<number>();
  const [dataUnit, setDataUnit] = useState<string>();
  const [readingTime, setReadingTime] = useState<string>();

  const [fontSize, setFontSize] = useState(16);
  const valueAreaRef = useRef<HTMLDivElement>(null);

  const [chartData, setChartData] = useState<GaugeData[]>(ChartData.chartData ?? []);
  const [chartScale, setChartScale] = useState<ChartScale>(
    (ChartData.chartScale === undefined || (ChartData.chartScale.y_axis === defaultYAxis)) ?
      { y_axis: { min_auto: true, max_auto: true, max: 10, min: 0 } }
      : ChartData.chartScale
  );

  const nodeData = useAppSelector(selectData);

  function getBlankRing() {
    return {
      label: "",
      colour: "#000000",
      phenomena: "new",
      ringID: uuidv4(),
      deviceID: "new",
      unit: "",
      min: 0,
      max: 10,
    };
  }

  function CatchBlankData() {
    if (chartData.length === 0) {
      setChartData(() => [
        ...ChartData.chartData,
        getBlankRing(),
      ]);
    } else {
      setChartData(ChartData.chartData ?? []);
    }
  }

  function GetDataAndReadingTime() {
    if (nodeData) {
      if (chartData !== undefined && chartData.length > 0) {
        if (
          nodeData?.data?.[chartData[0].deviceID as keyof DataType] !== undefined
        ) {
          var deviceNodeData = nodeData.data[chartData[0].deviceID as keyof DataType];
          let latestValue = deviceNodeData[deviceNodeData.length - 1]
          if (chartData[0].phenomena !== undefined && chartData[0].elemID !== undefined) {
            if (latestValue.data_points?.[chartData[0].phenomena]?.[chartData[0].elemID] !== undefined) {
              setDataValue(
                latestValue.data_points[chartData[0].phenomena][chartData[0].elemID]
              );
            }
          }
          setDataUnit(chartData[0].unit)
          setReadingTime(
            moment(new Date(latestValue.ts))
              .local()
              .format(
                "[" +
                language.charts.gauge.timeFormat.on +
                "] DD/MM/YYYY [" +
                language.charts.gauge.timeFormat.at +
                "] HH:mm:ss"
              )
          );
        }
      }
    }
  }

  function GetChartScaleFromParams() {
    if (ChartData.chartScale) {
      setChartScale(ChartData.chartScale);
    }
  }

  useEffect(() => {
    CatchBlankData();
  }, [ChartData.chartData]);

  useEffect(() => {
    GetDataAndReadingTime();
  }, [ChartData, chartData, nodeData]);

  useEffect(() => {
    GetChartScaleFromParams();
  }, [ChartData.chartScale]);

  function dataUnitFilter(dataUnit: string | undefined) {
    switch (dataUnit) {
      case undefined:
        return "";
      case "℃":
        return "°C";
      case "V":
        return "";
      case "mm/tip":
        return "mm";
      default:
        return dataUnit;
    }
  }

  const displayValue = !isNaN(Number(dataValue))
    ? Math.round((Number(dataValue) * 10)) / 10
    : "";
  const displayUnit = chartScale.y_axis.min_auto === true ? dataUnitFilter(dataUnit) : "";
  const displayText = `${displayValue}${displayUnit}`;

  // Recalculate font size whenever the tile is resized or the value text changes
  useEffect(() => {
    const valueArea = valueAreaRef.current;
    if (!valueArea) return;

    const update = () => {
      const { width, height } = valueArea.getBoundingClientRect();
      if (width <= 0 || height <= 0) return;
      // Fit within both height and width so long values / small tiles don't clip
      const byHeight = height * 0.7;
      const byWidth = (width * 0.9) / (Math.max(displayText.length, 1) * 0.6);
      setFontSize(Math.max(10, Math.floor(Math.min(byHeight, byWidth))));
    };

    const observer = new ResizeObserver(update);
    observer.observe(valueArea);
    update();
    return () => observer.disconnect();
  }, [displayText]);

  return (
    <>
      {chartData && (
        chartData[0] && (
          (!(chartData[0].deviceID && chartData[0].deviceID !== "new")) ? (
            <div className="w-full h-full flex items-center justify-center">
              Select a Device And Parameter to show data
            </div>
          ) : (
            (!(chartData[0].phenomena && chartData[0].elemID)) ? (
              <div className="w-full h-full flex items-center justify-center">
                Select a Parameter to show data
              </div>
            ) : (
              (!(dataValue || dataValue === 0)) ? (
                <div className="w-full h-full flex items-center justify-center">
                  <Spinner colour="fill-blue-600" />
                </div>
              ) : (
                <div className="absolute inset-0 flex flex-col overflow-hidden p-2">
                  <div className="truncate">
                    <p className="text-center font-semibold text-sm">{ChartData.title.en}</p>
                    <p className="text-center font-semibold text-xs">{chartScale.y_axis.max_auto === true && readingTime}</p>
                  </div>
                  <div ref={valueAreaRef} className="flex w-full flex-1 items-center justify-center overflow-hidden">
                    <span
                      className="whitespace-nowrap font-bold leading-none"
                      style={{ fontSize, color: chartData[0].colour, fontFamily: "sans-serif" }}
                    >
                      {displayText}
                    </span>
                  </div>
                </div>
              )
            )
          )
        )
      )}
    </>
  );
}

export default Value;
